import jsPDF from "jspdf";
import {
  PREMIUM_TIERS,
  ALA_CARTE,
  COMPASSION_PLAN,
  SLIDING_SCALE_TIERS,
  BOUNDARIES,
  POSITIONING,
} from "@/lib/fsap-data";
import {
  RETAINER_PDF_VERSION,
  RETAINER_PDF_CTA_PATH,
  RETAINER_PDF_LATEST_PATH,
} from "@/lib/retainer-pdf-version";

/** Brand palette (RGB) used for the branded retainer comparison PDF. */
const NAVY: [number, number, number] = [11, 20, 38];
const GOLD: [number, number, number] = [198, 158, 78];
const INK: [number, number, number] = [28, 32, 40];
const MUTED: [number, number, number] = [110, 118, 132];
const LINE: [number, number, number] = [216, 220, 228];

const PAGE_W = 612; // Letter, portrait, pt
const PAGE_H = 792;
const M = 44; // page margin

const CREDENTIALS = "PsyD · ThD · DMin · CERC";
const SITE = "drsullyspeaks.com";

function money(n: number) {
  return `$${n.toLocaleString()}`;
}

function header(doc: jsPDF, title: string, subtitle: string) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE_W, 104, "F");
  doc.setFillColor(...GOLD);
  doc.rect(0, 104, PAGE_W, 3, "F");

  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("DR. SULLY  ·  " + CREDENTIALS.toUpperCase(), M, 38);

  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "normal");
  doc.setFontSize(22);
  doc.text(title, M, 68);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(190, 197, 210);
  doc.text(subtitle, M, 86, { maxWidth: PAGE_W - M * 2 });
}

function footer(doc: jsPDF, page: number, pages: number, generated: string) {
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.6);
  doc.line(M, PAGE_H - 52, PAGE_W - M, PAGE_H - 52);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  doc.text(`${SITE}  ·  Full-Scope Advocacy Partner  ·  San Antonio, TX (travel available)`, M, PAGE_H - 38);
  doc.text(
    `v${RETAINER_PDF_VERSION}  ·  Generated ${generated}  ·  Page ${page} of ${pages}`,
    PAGE_W - M,
    PAGE_H - 38,
    { align: "right" },
  );
  doc.text(
    `Pricing is current at generation date and subject to written agreement. Not legal, medical, or therapeutic advice. Always confirm the latest version at ${SITE}${RETAINER_PDF_LATEST_PATH}.`,
    M,
    PAGE_H - 26,
    { maxWidth: PAGE_W - M * 2 },
  );
}

function sectionTitle(doc: jsPDF, text: string, y: number) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...GOLD);
  doc.text(text.toUpperCase(), M, y);
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(M, y + 5, M + 26, y + 5);
  return y + 20;
}

/** Builds the branded FSAP retainer tiers comparison PDF. */
export function buildRetainerComparisonPdf(): jsPDF {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const generated = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // ---------------- Page 1: retainer comparison ----------------
  header(
    doc,
    "FSAP Retainer Tiers — Comparison",
    "Full-Scope Advocacy Partner. Monthly retainers for ongoing advocacy, representation, and presence.",
  );

  let y = 136;
  doc.setFont("times", "italic");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  y += doc.splitTextToSize(POSITIONING, PAGE_W - M * 2).length * 13;
  doc.text(doc.splitTextToSize(POSITIONING, PAGE_W - M * 2), M, 148);
  y = 148 + y - 136 + 8;

  y = sectionTitle(doc, "Premium retainers", y + 6);

  const colW = (PAGE_W - M * 2 - 16) / 3;
  const colTop = y;
  let maxBottom = colTop;

  PREMIUM_TIERS.forEach((tier, i) => {
    const x = M + i * (colW + 8);
    let cy = colTop + 26;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...NAVY);
    doc.text(tier.name.toUpperCase(), x + 12, cy);

    cy += 22;
    doc.setFont("times", "bold");
    doc.setFontSize(20);
    doc.setTextColor(...INK);
    doc.text(money(tier.monthly), x + 12, cy);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("/month", x + 14 + doc.getTextWidth(money(tier.monthly)) * 0 + 52, cy);

    cy += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...INK);
    const tag = doc.splitTextToSize(tier.tagline, colW - 24);
    doc.text(tag, x + 12, cy);
    cy += tag.length * 10 + 8;

    doc.setDrawColor(...LINE);
    doc.line(x + 12, cy, x + colW - 12, cy);
    cy += 14;

    doc.setFontSize(7.6);
    tier.features.forEach((f) => {
      const lines = doc.splitTextToSize(f, colW - 32);
      doc.setTextColor(...GOLD);
      doc.text("•", x + 12, cy);
      doc.setTextColor(...INK);
      doc.text(lines, x + 22, cy);
      cy += lines.length * 9.6 + 5;
    });

    cy += 4;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.2);
    doc.setTextColor(...MUTED);
    const bf = doc.splitTextToSize(`Best for: ${tier.bestFor}`, colW - 24);
    doc.text(bf, x + 12, cy);
    cy += bf.length * 9 + 12;

    maxBottom = Math.max(maxBottom, cy);
  });

  // Draw the tier card borders once the tallest column is known.
  PREMIUM_TIERS.forEach((_, i) => {
    const x = M + i * (colW + 8);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.8);
    doc.rect(x, colTop, colW, maxBottom - colTop);
    doc.setFillColor(...GOLD);
    doc.rect(x, colTop, colW, 3, "F");
  });

  y = maxBottom + 26;

  y = sectionTitle(doc, "Accessible option", y);
  doc.setFillColor(248, 249, 251);
  const cpTop = y - 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text(`${COMPASSION_PLAN.name} — ${money(COMPASSION_PLAN.monthly)}/month`, M + 12, y + 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...INK);
  const cpLines = doc.splitTextToSize(
    `${COMPASSION_PLAN.purpose} Target household income ${COMPASSION_PLAN.targetIncome.replace(/[^\x00-\x7F]/g, "approx. ")}. Includes: ${COMPASSION_PLAN.features.join("; ")}.`,
    PAGE_W - M * 2 - 24,
  );
  doc.text(cpLines, M + 12, y + 24);
  const cpBottom = y + 24 + cpLines.length * 10 + 6;
  doc.setDrawColor(...LINE);
  doc.rect(M, cpTop, PAGE_W - M * 2, cpBottom - cpTop);

  // ---------------- Page 2: à la carte, sliding scale, boundaries ----------------
  doc.addPage();
  header(
    doc,
    "À La Carte, Sliding Scale & Terms",
    "Single-service pricing and the hardship pathways available alongside every retainer tier.",
  );

  y = sectionTitle(doc, "À la carte services", 146);

  const rowH = 26;
  doc.setDrawColor(...LINE);
  ALA_CARTE.forEach((item, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(248, 249, 251);
      doc.rect(M, y - 14, PAGE_W - M * 2, rowH, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...NAVY);
    doc.text(item.name, M + 10, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(item.notes ? `${item.unit} · ${item.notes}` : item.unit, M + 10, y + 10);
    doc.setFont("times", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    doc.text(item.price, PAGE_W - M - 10, y + 3, { align: "right" });
    y += rowH;
  });

  y += 22;
  y = sectionTitle(doc, "Sliding scale & scholarships", y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "normal");
  SLIDING_SCALE_TIERS.forEach((t) => {
    doc.setTextColor(...GOLD);
    doc.text("•", M, y);
    doc.setTextColor(...INK);
    doc.text(`${t.label} — ${t.income}`, M + 12, y);
    y += 15;
  });
  y += 10;

  y = sectionTitle(doc, "Boundaries & disclaimers", y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  BOUNDARIES.forEach((b) => {
    const lines = doc.splitTextToSize(b, PAGE_W - M * 2 - 14);
    doc.setTextColor(...GOLD);
    doc.text("•", M, y);
    doc.setTextColor(...INK);
    doc.text(lines, M + 12, y);
    y += lines.length * 11 + 6;
  });

  y += 16;
  doc.setFillColor(...NAVY);
  doc.rect(M, y, PAGE_W - M * 2, 62, "F");
  doc.setFillColor(...GOLD);
  doc.rect(M, y, 3, 62, "F");
  doc.setFont("times", "normal");
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text("Ready to talk it through?", M + 18, y + 26);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(200, 206, 218);
  doc.text(`Request a consultation at ${SITE}${RETAINER_PDF_CTA_PATH}`, M + 18, y + 44);
  doc.setFontSize(7.5);
  doc.setTextColor(170, 178, 194);
  doc.text(
    `Document v${RETAINER_PDF_VERSION} — newest copy always at ${SITE}${RETAINER_PDF_LATEST_PATH}`,
    M + 18,
    y + 56,
  );

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    footer(doc, p, pages, generated);
  }

  doc.setProperties({
    title: `FSAP Retainer Tiers Comparison v${RETAINER_PDF_VERSION} — Dr. Sully`,
    subject: "Full-Scope Advocacy Partner retainer tiers, à la carte pricing, and hardship options",
    author: `Dr. Sully, ${CREDENTIALS}`,
    keywords: `FSAP, advocacy, retainer, pricing, San Antonio, v${RETAINER_PDF_VERSION}`,
  });

  return doc;
}

/** Versioned filename so a re-download never silently overwrites an older copy. */
export function retainerComparisonPdfFilename() {
  return `FSAP_Retainer_Tiers_Comparison_v${RETAINER_PDF_VERSION}.pdf`;
}

/** Generates and downloads the branded retainer comparison PDF. */
export function downloadRetainerComparisonPdf() {
  const doc = buildRetainerComparisonPdf();
  doc.save(retainerComparisonPdfFilename());
}
